import React, {useEffect, useMemo, useState} from 'react';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  IconButton
} from '@mui/material';
import {Delete} from '@mui/icons-material';
import {useForm, useFieldArray, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {getValue, maxHeight, Stack} from '@mui/system';
import {DatePicker, DesktopDatePicker} from '@mui/x-date-pickers';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import SelectInput from '@components/form/select-input/SelectInput';
import {
  getLMSResultsData,
  getSearchResultsData
} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';

import EncryptUtils from 'src/utils/EncryptUtils';
import {useAppContext} from '@context/AppContext';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import {LoadingButton} from '@mui/lab';
import {FormHelperTextP} from '@components/form/select-input/SelectInputStyle';
import {FormControlDiv} from '../../../module-search-filter/ModuleSearchFilterStyle';
import {useLocation} from 'react-router-dom';
import {BASE_RECORD_ID, LMS_IS_ADMIN} from 'src/routes/BaseRoute';
import {useSnackbar} from 'notistack';
import moment from 'moment';
import {isValid} from 'date-fns';
import {useModulePreviewContext} from '@context/ModulePreviewContextProvider';

// Validation schema for entities
interface ModuleData {
  id: string;
  name: string;
  expanded: boolean;
  description?: string;
  duration?: string;
  type: string;
  sequence: number;
  module_lock_type_id?: any;
  locked_until_date?: string;
  Module_Completed?: boolean;
  IsUnlocked?: boolean;
  is_completed?: boolean;
  typeId: number;
  locked_linked_items?: {
    id: string;
    item_name: string;
    entity_completion_type_id: any;
    entity_completion_sub_type_id: any;
    module_link_id: any;
    module_id: any;
    type: 'file' | 'link' | 'content' | 'assignment' | 'onlinetest' | 'module';
  }[];
  files?: {
    id: string;
    name: string;
    ParentName: string;
    blob_file_extension?: string;
    fileexpanded: boolean;
    time?: string;
    score?: string;
    typeId: number;
    lock_type_id: number;
    locked_until_date?: string;
    type: string;
    module_id: any;
    sequence: number;
    ChildItems_Completed?: boolean;
    IsUnlocked?: boolean;
    is_completed?: boolean;
    additionalFiles?: {id: string; name: string}[];
    locked_linked_items?: {
      id: string;
      item_name: string;
      entity_completion_type_id: any;
      entity_completion_sub_type_id: any;
      module_link_id: any;
      module_id: any;
      type:
        | 'file'
        | 'link'
        | 'content'
        | 'assignment'
        | 'onlinetest'
        | 'module';
    }[];
  }[];
}
interface FormData {
  lockOption: number;
  lockUntil?: Date | null | undefined;
  entities?: {
    entity_id: number | undefined;
    entity_completion_type_id: number | null;
    entity_completion_sub_type_id: string | null;
  }[];
}
interface EntityValue {
  entity_id: string;
  entity_completion_type_id: string | null;
  entity_completion_sub_type_id: string | null;
}
interface DropDownData {
  Name: string;
  Values: {label: string; value: string}[];
}
interface LockPropertiesProps {
  uiFields?: any;
  Moduledata?: any;
  insertdata?: any;
  tableNameEntity?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  isloader?: boolean;
  dropdowndata?: DropDownData[];
}
const createValidationSchema = (formValuess: FormData) => {
  const {lockOption} = formValuess;
  if (lockOption === 2) {
    return yup.object({
      lockOption: yup.number().required(),
      lockUntil: yup.date().required('Lock date is required'),
      entities: yup.array().notRequired()
    });
  }

  if (lockOption === 3) {
    return yup.object({
      lockOption: yup.number().required(),
      lockUntil: yup.date().nullable(),
      entities: yup
        .array()
        .of(
          yup.object({
            entity_id: yup.number().notRequired(),
            entity_completion_type_id: yup
              .number()
              .required('Type is required'),
            entity_completion_sub_type_id: yup
              .string()
              .required('Value is required')
          })
        )
        .min(1, 'At least one entity is required')
        .required()
    });
  }

  return yup.object({
    lockOption: yup.number().required(),
    lockUntil: yup.date().nullable(),
    entities: yup.array().notRequired()
  });
};

const NewFilesLockProperties: React.FC<LockPropertiesProps> = ({
  uiFields,
  insertdata,
  tableNameEntity,
  Moduledata,
  isloader,
  dropdowndata
}) => {
  const {
    appState: {applicationName}
  } = useAppContext();
  const {enqueueSnackbar} = useSnackbar();
  function useQuery() {
    const {search} = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const lmsisAdmin = EncryptUtils.getDecryptedData(
    location?.search,
    LMS_IS_ADMIN
  );
  const query = useQuery();
  const section = query.get('section');
  const subSection = query.get('subsection');
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );
  // Initialize React Hook Form
  const [lockOption, setLockOption] = useState(1);
  const {ModulePreviewData, SetModulePreviewData} = useModulePreviewContext();

  const {dropDownData, setDropDownData} = useModulePreviewContext();
  const [entityData, setEntityData] = useState<DropDownData[]>([]);
  // Initialize Form Values
  const [entityValues, setEntityValues] = useState<EntityValue[]>([]);
  const [formValues, setFormValues] = useState<FormData>({
    lockOption: lockOption,
    lockUntil: null,
    entities: [
      {
        entity_id: undefined,
        entity_completion_type_id: null,
        entity_completion_sub_type_id: null
      }
    ]
  });
  const [isloaded, setIsLoaded] = useState(false);
  const [lockproploder, setLockproploader] = useState(false);
  const validationSchema = useMemo(
    () =>
      createValidationSchema({
        lockOption: lockOption,
        lockUntil: null,
        entities: [
          {
            entity_id: undefined,
            entity_completion_type_id: null,
            entity_completion_sub_type_id: null
          }
        ]
      }),
    [lockOption]
  );

  // Initialize React Hook Form
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      lockOption: lockOption,
      lockUntil: null,
      entities: []
    }
  });

  const {
    control,
    handleSubmit,
    setValue,
    resetField,
    getValues,
    watch,
    formState: {errors}
  } = methods;
  const {fields, append, remove} = useFieldArray({
    control,
    name: 'entities'
  });
  const values =
    dropDownData.find(item => item.Name === 'EntityCompletionType')?.Values ||
    [];

  // Your tree data: assume this is coming from your system

  // Normalize function for matching type
  // function normalizeType(type) {
  //   return type.toLowerCase().replace(/_/g, '');
  // }
  // const values = dropDownData.find(d => d.Name === 'EntityCompletionType')?.Values || [];

  const typeMap = new Map(
    values.map(({label, value}) => [
      label.toLowerCase().replace(/\s|_/g, ''),
      value.toString()
    ])
  );
  // Extract and group items by typeId (from typeMap)
  function extractDropDownDataFromTree(tree: ModuleData[]) {
    const typeIdToItems = new Map();
    for (const module of tree) {
      const modTypeId = typeMap.get(
        module.type?.toLowerCase().replace(/\s|_/g, '')
      );
      if (modTypeId) {
        if (!typeIdToItems.has(modTypeId)) typeIdToItems.set(modTypeId, []);
        if (module.id !== insertdata.data[0].module_id) {
          typeIdToItems.get(modTypeId).push({
            label: module.name,
            value: module.id
          });
        }
      }

      if (Array.isArray(module.files)) {
        for (const file of module.files) {
          const fileTypeId = typeMap.get(
            file.type?.toLowerCase().replace(/\s|_/g, '')
          );
          if (fileTypeId) {
            if (!typeIdToItems.has(fileTypeId))
              typeIdToItems.set(fileTypeId, []);
            if (file.id !== insertdata.data[0].inserted_id) {
              typeIdToItems.get(fileTypeId).push({
                label: file.name,
                value: file.id
              });
            } else if (
              file.id === insertdata.data[0].inserted_id &&
              file.type !== insertdata.data[0].type
            ) {
              typeIdToItems.get(fileTypeId).push({
                label: file.name,
                value: file.id
              });
            }
          }
        }
      }
    }

    return [...typeIdToItems.entries()].map(([Name, Values]) => ({
      Name,
      Values
    }));
  }

  const generateOptionsList = (fields: any, data: any) => {
    const transformedData = data
      .map((item: {name: any; id: any}) => ({
        label: item.name,
        value: item.id
      }))
      .sort(
        (a: {sequence: number}, b: {sequence: number}) =>
          a.sequence - b.sequence
      );

    return transformedData;
  };
  const handleEntityData = async () => {
    try {
      // const operations = 'DropDownData';

      // const {sql_data} = uiFields['EntityCompletionUpload'];
      // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
      const dropDownResults =
        await extractDropDownDataFromTree(ModulePreviewData);
      // setScreenSave(true);
      // const data: any = {
      //   sql_data: sql_data,
      //   data: {
      //     course_overview_id: uniqueId,
      //     module_id: insertdata.data[0].module_id,
      //     module_link_id: insertdata.data[0].inserted_id,
      //     tableName: 'ModuleOnlineTestCompletion'
      //   },
      //   operation: operations
      // };
      // const encryptRequestData = EncryptUtils.encryptedData(data);
      // const result = await getLMSResultsData(applicationName, 'LMS', {
      //   tk: encryptRequestData
      // });
      // const transformedData: DropDownData[] = Object.values(
      //   result.data.reduce(
      //     (
      //       acc: {[x: string]: {Name: any; Values: {label: any; value: any}[]}},
      //       curr: {ModuleContentType: any; label: any; value: any}
      //     ) => {
      //       const key = `ModuleContentType ${curr.ModuleContentType}`;
      //       if (!acc[key]) {
      //         acc[key] = {
      //           Name: `${curr.ModuleContentType}`,
      //           Values: []
      //         };
      //       }
      //       acc[key].Values.push({label: curr.label, value: curr.value});
      //       return acc;
      //     },
      //     {} as Record<string, DropDownData>
      //   )
      // );
      setEntityData(dropDownResults);
      return dropDownResults;
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };
  const handleLegacyAppChange = async (event: any, name: string) => {
    const {sql_data, order_by, sort_by} = uiFields[name];
    try {
      const data: any = {
        sql_data: sql_data,
        sort: order_by,
        sort_by: sort_by,
        isImportFiles: true
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const result = await getSearchResultsData(applicationName, 'LMS', {
        tk: encryptRequestData
      });
      const new_options = generateOptionsList(
        [{generate_options: true}],
        result?.data
      );
      setDropDownData((prevData: any) => {
        const existingEntry = prevData?.find(
          (entry: {Name: string}) => entry.Name === name
        );

        if (existingEntry) {
          // If the Name already exists, update its Values
          return [...prevData];
        }

        // If the Name is new, add it to the array
        return [...prevData, {Name: name, Values: new_options}];
      });
      return new_options;
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };
  const findFileInModule = (
    data: any[],
    moduleId: any,
    fileId: any,
    type: string
  ) => {
    const module = data.find(m => m.id === moduleId);
    if (!module) return null;

    const file = module.files?.find(
      (f: {id: any; type: string}) => f.id === fileId && f.type === type
    );
    return file ?? null;
  };
  const init = async () => {
    try {
      setLockproploader(true);
      if (uiFields) {
        const {sql_data} = uiFields['create_sql'];
        let operations;
        if (insertdata) {
          operations = 'SEARCH';
        }
        const data: any = {
          sql_data: sql_data,
          data: {
            id: insertdata.data[0].inserted_id,
            course_overview_id: uniqueId
          },
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        if (result.data[0].lock_type_id) {
          setLockOption(result.data[0].lock_type_id);
          setValue('lockOption', result.data[0].lock_type_id);
        }
        if (result.data[0].lock_type_id === 2) {
          if (result.data[0].locked_until_date) {
            setValue(
              'lockUntil',
              new Date(
                moment.utc(result.data[0].locked_until_date).toISOString()
              )
            ); // Parse ISO string to Date
          } else {
            setValue('lockUntil', null); // Handle null values
          }
        }
        if (
          result.data[0].lock_type_id === 3 &&
          result.data[0].entityfiles.length > 0
        ) {
          const updatedEntities = result.data[0].entityfiles.map(
            (entityFile: {
              entity_id: any;
              entity_completion_type_id: any;
              entity_completion_sub_type_id: any;
            }) => ({
              entity_id: entityFile.entity_id ?? undefined, // Assign entity_id
              entity_completion_type_id:
                entityFile.entity_completion_type_id ?? null, // Assign type_id
              entity_completion_sub_type_id:
                entityFile.entity_completion_sub_type_id ?? null // Assign sub_type_id
            })
          );
          setEntityValues(updatedEntities);
          // Update the form's entities field
          setValue('entities', updatedEntities, {shouldValidate: true});
          // `dropDownData` is assumed to be the array you showed above.
          const hasEntityCompletionType = dropDownData.some(
            item => item.Name === 'EntityCompletionType'
          );

          if (!hasEntityCompletionType) {
            await handleLegacyAppChange(
              'EntityCompletionType',
              'EntityCompletionType'
            );
          }
          await handleEntityData();
        }
        const hasEntityCompletionType = dropDownData.some(
          item => item.Name === 'lockOption'
        );

        if (!hasEntityCompletionType) {
          await handleLegacyAppChange('lockOption', 'lockOption');
        }
        setLockproploader(false);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setLockproploader(false);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      const fileData = findFileInModule(
        ModulePreviewData,
        insertdata.data[0].module_id,
        insertdata.data[0].inserted_id,
        insertdata.data[0].type
      );
      if (fileData) {
        setLockproploader(true);
        if (fileData.lock_type_id) {
          setLockOption(fileData.lock_type_id);
          setValue('lockOption', fileData.lock_type_id);
        }
        if (fileData.lock_type_id === 2) {
          if (fileData.locked_until_date) {
            setValue(
              'lockUntil',
              new Date(moment.utc(fileData.locked_until_date).toISOString())
            ); // Parse ISO string to Date
          } else {
            setValue('lockUntil', null); // Handle null values
          }
        }
        if (
          fileData.lock_type_id === 3 &&
          fileData.locked_linked_items.length > 0
        ) {
          const updatedEntities = fileData.locked_linked_items.map(
            (entityFile: {
              id: any;
              entity_completion_type_id: any;
              entity_completion_sub_type_id: any;
            }) => ({
              entity_id: entityFile.id ?? undefined, // Assign entity_id
              entity_completion_type_id:
                entityFile.entity_completion_type_id ?? null, // Assign type_id
              entity_completion_sub_type_id:
                entityFile.entity_completion_sub_type_id ?? null // Assign sub_type_id
            })
          );
          setEntityValues(updatedEntities);
          // Update the form's entities field
          setValue('entities', updatedEntities, {shouldValidate: true});
          // `dropDownData` is assumed to be the array you showed above.
          const hasEntityCompletionType = dropDownData.some(
            item => item.Name === 'EntityCompletionType'
          );

          if (!hasEntityCompletionType) {
            await handleLegacyAppChange(
              'EntityCompletionType',
              'EntityCompletionType'
            );
          }
          await handleEntityData();
        }
        const hasEntityCompletionType = dropDownData.some(
          item => item.Name === 'lockOption'
        );

        if (!hasEntityCompletionType) {
          await handleLegacyAppChange('lockOption', 'lockOption');
        }
        setLockproploader(false);
      } else {
        await init();
      }
    };
    fetchData();
  }, []);

  // console.log('control', control, entityValues);
  //   const [lockUntil, setLockUntil] = React.useState();
  // Handle form submission
  const onSubmit = async (datavalue: any) => {
    try {
      if (uiFields) {
        const dataform = {...datavalue};
        const {sql_data} = uiFields['create_sql'];
        dataform.id = insertdata.data[0].inserted_id;
        dataform.course_overview_id = uniqueId;
        dataform.isAdmin = lmsisAdmin === 'True';
        dataform.page = 2;
        setIsLoaded(true);
        const data: any = {
          sql_data: sql_data,
          data: dataform,
          operation: 'UPDATE'
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        const processModulesData = (data: ModuleData[]) => {
          return data.map(module => ({
            ...module,
            files:
              module.files?.map(file => ({
                ...file,
                locked_linked_items:
                  typeof file.locked_linked_items === 'string'
                    ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
                    : file.locked_linked_items || [], // Keep array or default to empty
                additionalFiles:
                  typeof file.additionalFiles === 'string'
                    ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
                    : file.additionalFiles || [] // Keep array or default to empty
              })) || []
          }));
        };
        const syncExpandedStates = (
          transformed: ModuleData[],
          existing: ModuleData[]
        ) => {
          return transformed.map(module => {
            const matchingModule = existing.find(e => e.id === module.id);

            if (!matchingModule) return module;

            // Sync the `expanded` flag
            const updatedModule = {
              ...module,
              expanded: matchingModule.expanded || false,
              files:
                module?.files &&
                module?.files.map(file => {
                  const matchingFile =
                    matchingModule?.files &&
                    matchingModule.files.find(f => f.id === file.id);
                  return {
                    ...file,
                    fileexpanded: matchingFile?.fileexpanded || false
                  };
                })
            };

            return updatedModule;
          });
        };
        if (getValues('lockOption') === 3) {
          const {sql_data} = uiFields['EntityCompletionUpload'];
          const dataUploadForm = {...datavalue}; // Clone datavalue for lockOption-specific logic

          // Ensure id is not added for lockOption === 3
          delete dataUploadForm.id;

          dataUploadForm.module_id = insertdata.data[0].module_id; // Add module_id instead
          setIsLoaded(true);
          let deleteData;
          const entities = watch('entities'); // Get current entities from the form
          if (entityValues !== undefined) {
            if (entities && entityValues && entityValues?.length > 0) {
              const updatedEntities = entities.map(entity => {
                return entity;
              });
              // const resultEntityIds = entityValues?.map(res => res.entity_id);
              const entitiesToDelete = entityValues?.filter((res: any) => {
                // Check if the entity ID exists in the current entities list
                return !updatedEntities.some(
                  entity => entity?.entity_id === res?.entity_id
                );
              });
              // Update entities for deletion
              deleteData = entitiesToDelete.map(res => ({
                entity_id: res?.entity_id,
                deleted: true
              }));

              // Existing records without deleted flag

              // Use setValue to update the entities field
              setValue('entities', updatedEntities, {shouldValidate: true});
            }
          }
          const insertData = entities?.filter(entity => !entity.entity_id); // New records without an ID
          const updateData = entities?.filter(
            entity => entity.entity_id && !entity.deleted
          ); // Existing records with modifications
          // const deleteData = entities?.filter(entity => entity.deleted); // Records marked for deletion

          // Perform INSERT operations
          if (insertData && insertData?.length > 0) {
            const cleanedInsertData = insertData.map(item => ({
              ...item,
              module_id: insertdata.data[0].module_id, // Add module_id
              module_link_id: insertdata.data[0].inserted_id,
              tableName: tableNameEntity,
              isAdmin: lmsisAdmin === 'True',
              course_overview_id: uniqueId
            }));
            const insertRequest: any = {
              sql_data: sql_data,
              data: {
                entity: JSON.stringify(insertData),
                course_overview_id: uniqueId,
                module_link_id: insertdata.data[0].inserted_id,
                isAdmin: lmsisAdmin === 'True',
                tableName: tableNameEntity,
                module_id: insertdata.data[0].module_id
              },
              operation: 'INSERT',
              dataUpload: 'Multiple'
            };
            const insertEncryptRequest =
              EncryptUtils.encryptedData(insertRequest);
            const insertResult = await getLMSResultsData(
              applicationName,
              'LMS',
              {
                tk: insertEncryptRequest
              }
            );
            const transformData = await processModulesData(
              insertResult.module_data
            );
            const updatedTransformed = syncExpandedStates(
              transformData,
              ModulePreviewData
            );
            await SetModulePreviewData(updatedTransformed);
            const entities = watch('entities'); // Get current entities from the form
            if (entities) {
              const updatedEntities = entities.map(entity => {
                // Find the corresponding result for the current entity
                const match = insertResult.data.find(
                  (res: {entity_id: any}) => {
                    return (
                      res.entity_id === entity.entity_id || !entity.entity_id
                    );
                  }
                );

                // If a match is found, assign the entity_id from the result
                if (match) {
                  return {
                    ...entity,
                    entity_id: match.entity_id // Update the entity_id
                  };
                }

                // If no match is found, return the entity as-is
                return entity;
              });
              setEntityValues(updatedEntities);
              // Use setValue to update the entities field
              await setValue('entities', updatedEntities, {
                shouldValidate: true
              });
            }
          }

          // Perform UPDATE operations
          if (updateData && entities && updateData?.length > 0) {
            const cleanedUpdateData = updateData.map(
              ({entity_id, ...rest}) => ({
                entity_id,
                course_overview_id: uniqueId,
                tableName: tableNameEntity, // Only keep required fields
                ...rest
              })
            );
            const newentities = watch('entities');
            const updateRequest: any = {
              sql_data: sql_data,
              data: {
                entity: JSON.stringify(updateData),
                course_overview_id: uniqueId,
                tableName: tableNameEntity,
                isAdmin: lmsisAdmin === 'True'
              },
              operation: 'UPDATE',
              dataUpload: 'Multiple'
            };
            const updateEncryptRequest =
              EncryptUtils.encryptedData(updateRequest);
            const updateResult = await getLMSResultsData(
              applicationName,
              'LMS',
              {
                tk: updateEncryptRequest
              }
            );
            const transformData = await processModulesData(
              updateResult.module_data
            );
            const updatedTransformed = syncExpandedStates(
              transformData,
              ModulePreviewData
            );
            await SetModulePreviewData(updatedTransformed);
            if (newentities) {
              setEntityValues(newentities);
            }
          }

          // Perform DELETE operations
          if (deleteData && deleteData?.length > 0) {
            const cleanedDeleteData = deleteData.map(({entity_id}) => ({
              entity_id,
              course_overview_id: uniqueId,
              tableName: tableNameEntity // Only send entity_id for deletion
            }));
            const deleteRequest: any = {
              sql_data: sql_data,
              data: {
                entity: JSON.stringify(deleteData),
                course_overview_id: uniqueId,
                tableName: tableNameEntity,
                isAdmin: lmsisAdmin === 'True'
              },
              operation: 'DELETE',
              dataUpload: 'Multiple'
            };
            const deleteEncryptRequest =
              EncryptUtils.encryptedData(deleteRequest);
            const deleteResult = await getLMSResultsData(
              applicationName,
              'LMS',
              {
                tk: deleteEncryptRequest
              }
            );
            const transformData = await processModulesData(
              deleteResult.module_data
            );
            const updatedTransformed = syncExpandedStates(
              transformData,
              ModulePreviewData
            );
            await SetModulePreviewData(updatedTransformed);
            if (entities) {
              setEntityValues(entities);
            } else {
              setEntityValues([]);
            }
          }
        }
        if (getValues('lockOption') === 1 || getValues('lockOption') === 2) {
          if (result.data[1]) {
            const transformData = await processModulesData(result.data[1]);
            const updatedTransformed = syncExpandedStates(
              transformData,
              ModulePreviewData
            );
            await SetModulePreviewData(updatedTransformed);
          }
        }
        setIsLoaded(false);
      }
      enqueueSnackbar('Properties are saved', SnackBarConfig.getSuccess());
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
    // handleLockProperties(data)
  };
  return lockproploder ? (
    <Stack
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      minHeight={'50vh'}>
      <LoadingScreen />
    </Stack>
  ) : (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{maxHeight: '80vh', overflowY: 'auto', padding: '2vh'}}>
        <Grid container spacing={3}>
          {/* Module Lock Description */}
          <Grid item xs={12}>
            <Typography variant="h6">Module Lock</Typography>
            <Typography variant="body2">
              Specify when this module is to be unlocked and made available for
              learner.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="lockOption"
              control={control}
              render={({field: {value, onChange}}) => (
                <FormControlDiv>
                  <SelectInput
                    enableDefaultOption
                    name="lockOption"
                    options={
                      dropDownData.find(item => item.Name === 'lockOption')
                        ?.Values || []
                    }
                    value={value || lockOption}
                    fullWidth
                    onOpenCallback={(event: any) => {
                      const hasEntityCompletionType = dropDownData.some(
                        item => item.Name === 'lockOption'
                      );

                      if (!hasEntityCompletionType) {
                        handleLegacyAppChange(event, 'lockOption');
                      }
                    }}
                    onChange={(event: any) => {
                      onChange(event);
                      setLockOption(event.target.value);
                      resetField('lockUntil');
                      resetField('entities');
                      // setState(prevState => ({
                      //   ...prevState,
                      //   formError: ''
                      // }));
                    }}
                    optionValueKey={'value'}
                    optionLabelKey={'label'}
                    fetchOptionsFromOnOpenTrigger
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: '30vh', // Set dropdown max height
                          overflowY: 'auto' // Enable scrolling
                        }
                      }
                    }}
                  />
                </FormControlDiv>
              )}
            />
          </Grid>

          {/* Entity Selection */}
          {lockOption === 3 && (
            <Grid item xs={12}>
              <Typography variant="h6">Select Entity</Typography>
              <Typography variant="body2" sx={{mb: 2}}>
                The course entity will be unlocked on successful completion of
                the entities selected below.
              </Typography>

              {fields.map((field, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 2,
                    m: 1,
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0'
                  }}>
                  <Grid
                    container
                    spacing={2}
                    key={field.id}
                    alignItems="center">
                    <Grid item xs={4}>
                      <Controller
                        name={
                          `entities.${index}.entity_completion_type_id` as const
                        }
                        control={control}
                        defaultValue={field?.default}
                        render={({field: {value, onChange}}) => (
                          <FormControlDiv>
                            <SelectInput
                              sx={{
                                maxWidth: '30vh',
                                maxHeight: '30vh',
                                backgroundColor: 'white'
                              }}
                              name={
                                `entities.${index}.entity_completion_type_id` as const
                              }
                              options={
                                dropDownData.find(
                                  item => item.Name === 'EntityCompletionType'
                                )?.Values || []
                              }
                              label="select"
                              value={value || ''}
                              fullWidth
                              onOpenCallback={(event: any) => {
                                const hasEntityCompletionType =
                                  dropDownData.some(
                                    item => item.Name === 'EntityCompletionType'
                                  );

                                if (!hasEntityCompletionType) {
                                  handleLegacyAppChange(
                                    event,
                                    'EntityCompletionType'
                                  );
                                }
                              }}
                              onChange={(event: any) => {
                                onChange(event);
                                setValue(
                                  `entities.${index}.entity_completion_sub_type_id`,
                                  null
                                );
                                handleEntityData();
                              }}
                              optionValueKey={'value'}
                              optionLabelKey={'label'}
                              fetchOptionsFromOnOpenTrigger
                              onChangeCallback={(event: any) =>
                                console.log('eve', event)
                              }
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: '30vh', // Set dropdown max height
                                    overflowY: 'auto' // Enable scrolling
                                  }
                                }
                              }}
                            />
                          </FormControlDiv>
                        )}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <Typography
                        sx={{display: 'flex', justifyContent: 'center'}}>
                        is
                      </Typography>
                    </Grid>
                    {/* Entity Value */}
                    <Grid item xs={6}>
                      <Controller
                        name={
                          `entities.${index}.entity_completion_sub_type_id` as any
                        }
                        control={control}
                        render={({field: {value, onChange}}) => {
                          const entityCompletionTypeId = watch(
                            `entities.${index}.entity_completion_type_id`
                          );
                          // Get all options based on `entityCompletionTypeId`
                          const allOptions =
                            entityData.find(
                              item =>
                                item.Name === String(entityCompletionTypeId)
                            )?.Values || [];

                          // Exclude currently selected values from the options
                          const selectedValuesd =
                            watch('entities') // Watch all entities
                              ?.filter(
                                (e: any, i: number) =>
                                  i !== index ||
                                  entityCompletionTypeId !==
                                    e.entity_completion_type_id
                              ) || []; // Exclude the current entity
                          const selectedValues = selectedValuesd.map(
                            e =>
                              e?.entity_completion_type_id ==
                                entityCompletionTypeId &&
                              e?.entity_completion_sub_type_id
                          ); // Extract selected values
                          const filteredOptions = allOptions.filter(
                            option => !selectedValues?.includes(option.value)
                          );
                          return (
                            <FormControlDiv>
                              <SelectInput
                                sx={{
                                  maxHeight: '30vh',
                                  backgroundColor: 'white'
                                }}
                                name={
                                  `entities.${index}.entity_completion_sub_type_id` as const
                                }
                                options={filteredOptions}
                                label="select"
                                value={value || ''}
                                fullWidth
                                onOpenCallback={(event: any) =>
                                  handleEntityData()
                                }
                                onChange={(event: any) => {
                                  onChange(event);
                                }}
                                optionValueKey={'value'}
                                optionLabelKey={'label'}
                                fetchOptionsFromOnOpenTrigger
                                onChangeCallback={(event: any) =>
                                  console.log('eve', event)
                                }
                                error={
                                  !!(errors.entities?.[index] as any)
                                    ?.entity_completion_sub_type_id?.message
                                }
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: '30vh', // Set dropdown max height
                                      overflowY: 'auto' // Enable scrolling
                                    }
                                  }
                                }}
                              />
                            </FormControlDiv>
                          );
                        }}
                      />
                    </Grid>

                    {fields.length > 1 && index != 0 && (
                      <Grid item xs={1}>
                        <IconButton
                          color="error"
                          onClick={() => remove(index)}
                          aria-label="delete entity">
                          <Delete />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}
              {errors.entities?.message && (
                <FormHelperTextP>{errors.entities?.message}</FormHelperTextP>
              )}
              {/* Add Button */}
              <Box sx={{mt: 2}}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    append({
                      entity_id: undefined,
                      entity_completion_type_id: '',
                      entity_completion_sub_type_id: ''
                    })
                  }
                  sx={{
                    textTransform: 'none'
                  }}>
                  + Add
                </Button>
              </Box>
            </Grid>
          )}
          {lockOption === 2 && (
            <Grid item xs={12} md={6}>
              <Controller
                name="lockUntil"
                control={control}
                render={({field, fieldState: {error}}) => {
                  // Convert the string value to Date only if valid
                  const valueAsDate =
                    field.value && isValid(new Date(field.value))
                      ? new Date(field.value)
                      : null;

                  return (
                    <DatePicker
                      {...field}
                      value={valueAsDate}
                      format="dd/MM/yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message
                        }
                      }}
                    />
                  );
                }}
              />
            </Grid>
          )}

          {/* Save Button */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-start">
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                loading={isloaded}
                sx={{
                  textTransform: 'none'
                }}>
                Save
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </form>
    </LocalizationProvider>
  );
};

export default NewFilesLockProperties;
